import * as React from 'react';
import { PolymorphicRef } from '../../../utils/PolymorphicTypes';
import { CardProps, CardComponent } from './CardTypes';
import { useCardTheme } from '../../../themes';
import { CardInputValidationError } from './CardErrors';
import { useHover } from '@react-aria/interactions';

export const Card: CardComponent = React.forwardRef(
  <C extends React.ElementType = 'div'>({
  as,
  backgroundColor,
  borderColor,
  children,
  colorStyle="flat",
  disabled=false,
  id,
  onClick,
  selected=false,
  ...htmlProps
}:CardProps<C>, 
ref?:PolymorphicRef<C>) => {
  /* @start Input validation */
  if (!children) {
      throw new CardInputValidationError('`children` prop cannot be empty');
      }
  /* @end Input validation */

  const isLink = as && ((typeof as === 'string' && as === 'a')
  || (typeof as === 'object')) ? true : false;

  const Component = (disabled && (!as || isLink)) ? 'div' : (as || 'div');
  const { cardTheme } = useCardTheme(); 
  const _style = {
    backgroundColor: backgroundColor ? cardTheme[`${backgroundColor.replace('$', '')}`] : undefined,
    borderColor: colorStyle.toLowerCase() !== 'elevated' && borderColor ? cardTheme[`${borderColor.replace('$', '')}`] : undefined
  }

  let { hoverProps, isHovered } = useHover({});
  const isClickable = !disabled && (as && ((typeof as === 'string' && as === 'button') || isLink));

  const cardClasses = [
    cardTheme['bsw-card'], 
    cardTheme[`bsw-card-color--${colorStyle.toLowerCase()}`],
    disabled ? cardTheme['bsw-card--disabled'] : undefined,
    selected ? cardTheme['bsw-card--selected'] : undefined,
    isClickable ? cardTheme['bsw-card--button'] : undefined,
    isClickable && isHovered ? cardTheme['bsw-card--hover'] : undefined,
    htmlProps?.className
  ].join(' ');

     // Include disabled for elements that it is a valid attribute
     const _showDisabledAttr=(as && typeof as === 'string' && (as === 'button' || as === 'input')) ? disabled : undefined;
     const componentProps = !disabled
     ? {
        ...htmlProps,
        ...hoverProps,
        onClick:onClick,
      }
    : {
      disabled: _showDisabledAttr
    };

  return (
    <Component
      {...componentProps}
      className={cardClasses}
      id={id}
      ref={ref}
      style={_style}
    >
      {children}
    </Component>
  );
});
